/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

export type MermaidFlowchartDirection = 'BT' | 'LR' | 'RL' | 'TB';

export type MermaidFlowchartNodeVariant =
    | 'application'
    | 'component'
    | 'context'
    | 'external'
    | 'route';

export interface MermaidFlowchartNode {
    description?: string;
    label: string;
    variant?: MermaidFlowchartNodeVariant;
}

export type MermaidFlowchartNodeValue = MermaidFlowchartNode | string;

export interface MermaidDiagramGroup {
    label: string;
    nodes: Readonly<Record<string, MermaidFlowchartNodeValue>>;
}

export interface MermaidFlowchartDefinition {
    classDefinitions?: Readonly<Partial<Record<MermaidFlowchartNodeVariant, string>>>;
    direction: MermaidFlowchartDirection;
    edges: Readonly<Record<string, readonly string[]>>;
    groups?: Readonly<Record<string, MermaidDiagramGroup>>;
    nodes: Readonly<Record<string, MermaidFlowchartNodeValue>>;
}

function getNodeDefinition(node: MermaidFlowchartNodeValue): MermaidFlowchartNode {
    return typeof node === 'string' ? { label: node } : node;
}

function escapeNodeText(value: string): string {
    return value
        .replaceAll('&', '&amp;')
        .replaceAll('"', '&quot;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;');
}

function formatNode([id, value]: [string, MermaidFlowchartNodeValue]): string {
    const { description, label } = getNodeDefinition(value);
    const formattedLabel = description
        ? escapeNodeText(label) + '<br/><small>' + escapeNodeText(description) + '</small>'
        : escapeNodeText(label);

    return id + '["' + formattedLabel + '"]';
}

export function createMermaidFlowchart({
    classDefinitions = {},
    direction,
    edges,
    groups = {},
    nodes,
}: MermaidFlowchartDefinition): string {
    const groupEntries = Object.entries(groups);
    const groupedNodes = groupEntries.reduce<Record<string, MermaidFlowchartNodeValue>>(
        (groupNodes, [, group]) => ({ ...groupNodes, ...group.nodes }),
        {},
    );
    const allNodes: Record<string, MermaidFlowchartNodeValue> = {
        ...nodes,
        ...groupedNodes,
    };
    const nodeIds = new Set(Object.keys(allNodes));

    Object.entries(edges).forEach(([from, targets]) => {
        targets.forEach((to) => {
            if (!nodeIds.has(from) || !nodeIds.has(to)) {
                throw new Error('Mermaid edge references an unknown node: ' + from + ' --> ' + to);
            }
        });
    });

    const nodeEntries = Object.entries(nodes);
    const groupDefinitions = groupEntries.flatMap(([id, group]) => [
        'subgraph ' + id + '["' + group.label.replaceAll('"', '&quot;') + '"]',
        ...Object.entries(group.nodes).map((node) => '    ' + formatNode(node)),
        'end',
    ]);
    const nodeVariants = Object.entries(allNodes).reduce<Record<MermaidFlowchartNodeVariant, string[]>>(
        (variants, [id, node]) => {
            const variant = getNodeDefinition(node).variant;

            if (variant) {
                variants[variant].push(id);
            }

            return variants;
        },
        {
            application: [],
            component: [],
            context: [],
            external: [],
            route: [],
        },
    );
    const classStatements = [
        nodeVariants.application.length > 0
            ? 'class ' + nodeVariants.application.join(',') + ' application;'
            : null,
        nodeVariants.component.length > 0
            ? 'class ' + nodeVariants.component.join(',') + ' component;'
            : null,
        nodeVariants.context.length > 0
            ? 'class ' + nodeVariants.context.join(',') + ' context;'
            : null,
        nodeVariants.external.length > 0
            ? 'class ' + nodeVariants.external.join(',') + ' external;'
            : null,
        nodeVariants.route.length > 0
            ? 'class ' + nodeVariants.route.join(',') + ' route;'
            : null,
        nodeVariants.application.length > 0 && classDefinitions.application
            ? 'classDef application ' + classDefinitions.application + ';'
            : null,
        nodeVariants.component.length > 0 && classDefinitions.component
            ? 'classDef component ' + classDefinitions.component + ';'
            : null,
        nodeVariants.context.length > 0 && classDefinitions.context
            ? 'classDef context ' + classDefinitions.context + ';'
            : null,
        nodeVariants.external.length > 0 && classDefinitions.external
            ? 'classDef external ' + classDefinitions.external + ';'
            : null,
        nodeVariants.route.length > 0 && classDefinitions.route
            ? 'classDef route ' + classDefinitions.route + ';'
            : null,
    ].filter((definition): definition is string => definition !== null);

    return [
        'flowchart ' + direction,
        ...nodeEntries.map(formatNode),
        ...groupDefinitions,
        ...Object.entries(edges).flatMap(([from, targets]) =>
            targets.map((to) => from + ' --> ' + to),
        ),
        ...classStatements,
    ].join('\n');
}
